import React, { useEffect, useState, useRef, ReactNode, } from "react";
import { createPortal, } from "react-dom";

export type PopperPlacementType =
  | "bottom-end" | "bottom-start" | "bottom"
  | "left-end" | "left-start" | "left"
  | "right-end" | "right-start" | "right"
  | "top-end" | "top-start" | "top";

export type TransitionPropsType = {
  in: boolean;
};

export type PopperChildrenFunction = (props: {
  placement: PopperPlacementType;
  TransitionProps: TransitionPropsType;
}) => ReactNode;

export type PopperProps = Omit<React.HTMLAttributes<HTMLDivElement>, "children"> & {
  anchorEl?: HTMLElement | null;
  children: ReactNode | PopperChildrenFunction;
  disablePortal?: boolean;
  keepMounted?: boolean;
  open: boolean;
  placement?: PopperPlacementType;
  transition?: boolean;
};

const getPopperStyle = (
  anchorRect: DOMRect,
  popperRect: DOMRect,
  placement: PopperPlacementType
): React.CSSProperties => {
  let top = 0;
  let left = 0;
  const scrollY = window.scrollY;
  const scrollX = window.scrollX;
  const [mainAxis, align] = placement.split("-");

  if (mainAxis === "bottom") top = anchorRect.bottom + scrollY;
  if (mainAxis === "top") top = anchorRect.top - popperRect.height + scrollY;
  if (mainAxis === "left") left = anchorRect.left - popperRect.width + scrollX;
  if (mainAxis === "right") left = anchorRect.right + scrollX;

  if (mainAxis === "bottom" || mainAxis === "top") {
    if (align === "start") left = anchorRect.left + scrollX;
    else if (align === "end") left = anchorRect.right - popperRect.width + scrollX;
    else left = anchorRect.left + anchorRect.width / 2 - popperRect.width / 2 + scrollX;
  }

  if (mainAxis === "left" || mainAxis === "right") {
    if (align === "start") top = anchorRect.top + scrollY;
    else if (align === "end") top = anchorRect.bottom - popperRect.height + scrollY;
    else top = anchorRect.top + anchorRect.height / 2 - popperRect.height / 2 + scrollY;
  }

  return { top: `${top}px`, left: `${left}px` };
};

export default function Popper({
  anchorEl,
  children,
  disablePortal = false,
  keepMounted = false,
  open,
  placement = "bottom",
  transition = false,
  className = "",
  ...props
}: PopperProps): JSX.Element {
  const popperRef = useRef<HTMLDivElement>(null);
  const [styles, setStyles] = useState<React.CSSProperties>({ top: 0, left: 0, opacity: 0 });

  useEffect(() => {
    if (!open || !anchorEl || !popperRef.current) return;

    const updatePosition = () => {
      if (anchorEl && popperRef.current) {
        const anchorRect = anchorEl.getBoundingClientRect();
        const popperRect = popperRef.current.getBoundingClientRect();
        const newStyles = getPopperStyle(anchorRect, popperRect, placement);

        setStyles({ ...newStyles, opacity: 1 });
      }
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorEl, open, placement]);

  if (!keepMounted && !open) return <></>;

  const childProps = {
    placement,
    TransitionProps: { in: open },
  };

  const content = typeof children === "function" ? children(childProps) : children;

  const popperContent = (
    <div
      ref={popperRef}
      className={`absolute z-50 ${!open && !transition ? "pointer-events-none opacity-0" : ""} ${className}`}
      style={{
        ...styles,
        visibility: !open && keepMounted ? "hidden" : "visible",
      }}
      {...props}
    >
      {content}
    </div>
  );

  if (disablePortal) {
    return popperContent;
  }

  if (typeof document !== "undefined") {
    return createPortal(popperContent, document.body);
  }

  return <></>;
}